import React, { useEffect, useState } from 'react';
import { ClientApiProps } from '../common';
import {
  Acma_amateurradiocallsignavailability,
  Acma_amateurradiocallsignprefixes,
  Acma_amateurradiocallsigntypes,
  Acma_amateurradiolocations,
  Acma_amateurradioreservationtypes,
  IRegistryCallsign,
  getApiClientWihoutAuth,
} from '../api';
import { Button, DataGrid, Label, Select, SelectOption, Spinner, TextInput, RadioListOption } from 'acma-spa';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getLocationOptions, getTypeOptions } from '../forms/CallsignApplication/Page';

export const getAvailabilityOptions = () => {
  const options: SelectOption<Acma_amateurradiocallsignavailability>[] = [];
  for (const enumMember in Acma_amateurradiocallsignavailability) {
    options.push({ value: enumMember as Acma_amateurradiocallsignavailability, label: enumMember });
  }
  return options;
};

export const getPrefixOptions = () => {
  const options: SelectOption<Acma_amateurradiocallsignprefixes>[] = [];
  for (const enumMember in Acma_amateurradiocallsignprefixes) {
    if (enumMember == Acma_amateurradiocallsignprefixes.SpecialEvent) {
      options.push({ value: enumMember as Acma_amateurradiocallsignprefixes, label: 'Special event' });
    } else {
      options.push({ value: enumMember as Acma_amateurradiocallsignprefixes, label: enumMember });
    }
  }
  return options;
};

export const CallsignsRegistry: React.FC<ClientApiProps> = (props) => {
  const [availability, setAvailability] = useState<Acma_amateurradiocallsignavailability | null>(null);
  const [prefix, setPrefix] = useState<Acma_amateurradiocallsignprefixes | null>(null);
  const [location, setLocation] = useState<Acma_amateurradiolocations | null>(null);
  const [type, setType] = useState<Acma_amateurradiocallsigntypes | null>(null);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    setPageNumber(1);
  }, [prefix, location, type, search]);

  useEffect(() => {
    setType(null);
    if (prefix == Acma_amateurradiocallsignprefixes.SpecialEvent) {
      setLocation(null);
    }
  }, [prefix]);

  const [items, setItems] = useState<IRegistryCallsign[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    const [client, abort] = getApiClientWihoutAuth(props.baseUrl);

    if (pageNumber == 1) {
      setLoading(true);
      setItems([]);
      setHasMore(false);
    }

    client
      .callsignRegistryGet(availability ?? undefined, prefix ?? undefined, location ?? undefined, type ?? undefined, search, pageNumber)
      .then((d) => {
        setItems(pageNumber == 1 ? d : items.concat(d));
        setHasMore(d.length == 100);
      })
      .finally(() => setLoading(false));

    return function () {
      abort.abort();
    };
  }, [availability, prefix, location, type, search, pageNumber]);

  const onPaginate = () => {
    if (items && items.length > 0) {
      setPageNumber(pageNumber + 1);
    }
  };

  const getPageTypeOptions = function (prefix: Acma_amateurradiocallsignprefixes | undefined, noDescription?: boolean) {

    let options: RadioListOption<Acma_amateurradiocallsigntypes>[];


    if (prefix == Acma_amateurradiocallsignprefixes.VK) {
      const commonOptions = getTypeOptions(prefix ?? undefined, true);

      options = [...commonOptions, {
        value: Acma_amateurradiocallsigntypes.FSeries,
        label: 'F series call sign',
      }];
    }
    else {
      options = getTypeOptions(prefix ?? undefined, true);
    }

    return options;
  }

  const typeOptions = getPageTypeOptions(prefix ?? undefined, true);

  return (
    <>
      <div className="row mb-3">
        <div className="col-2">
          <Label label="Availability" id="availability">
            <Select<Acma_amateurradiocallsignavailability>
              options={getAvailabilityOptions()}
              value={availability}
              onChange={(v) => setAvailability(v)}
              defaultLabel="Select..."
              size="block"
            />
          </Label>
        </div>
        <div className="col-2">
          <Label label="Prefix" id="prefix">
            <Select<Acma_amateurradiocallsignprefixes>
              options={getPrefixOptions()}
              value={prefix}
              onChange={(v) => setPrefix(v)}
              defaultLabel="Select..."
              size="block"
            />
          </Label>
        </div>
        <div className="col-3">
          {prefix != Acma_amateurradiocallsignprefixes.SpecialEvent ? (
            <Label label="Location" id="location">
              <Select<Acma_amateurradiolocations>
                options={getLocationOptions()}
                value={location}
                onChange={(v) => setLocation(v)}
                defaultLabel="Select..."
                size="block"
              />
            </Label>
          ) : (
            <></>
          )}
        </div>
        <div className="col-2">
          {typeOptions.length > 0 ? (
            <Label label="Type" id="type">
              <Select<Acma_amateurradiocallsigntypes>
                options={typeOptions as SelectOption<Acma_amateurradiocallsigntypes>[]}
                value={type}
                onChange={(v) => setType(v)}
                defaultLabel="Select..."
                size="block"
              />
            </Label>
          ) : (
            <></>
          )}
        </div>
        <div className="col-3">
          <Label label="Search" id="search">
            <TextInput value={search} onChange={setSearch} onEnter={setSearch} placeholder="Enter call sign" />
          </Label>
        </div>
      </div>

      <Spinner loadingText="Loading..." isLoading={isLoading}>
        <InfiniteScroll
          dataLength={items.length} //This is important field to render the next data
          next={onPaginate}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}>
          <DataGrid<IRegistryCallsign>
            rows={items}
            columns={[
              { label: 'Call sign', value: (r) => r.callsignName },
              {
                label: 'Status',
                value: (r) => (
                  <>
                    {r.availability == Acma_amateurradiocallsignavailability.Available && r.reservedTo ? `Reserved to ${r.reservedTo}` : `${r.availability}`}
                    {r.availability != Acma_amateurradiocallsignavailability.Available && r.availability != Acma_amateurradiocallsignavailability.Pending && r.reservedTo ? ` to ${r.reservedTo}` : ''}{' '}
                  </>
                ),
              },
              {
                label: '',
                value: (r) =>
                  r.availability != Acma_amateurradiocallsignavailability.Assigned && r.availability != Acma_amateurradiocallsignavailability.Pending ? (
                    <Button
                      label="Apply"
                      mode="secondary"
                      className="pull-right py-0"
                      onClick={() =>
                      (document.location = `/acma-assist#/forms/amateurradio/callsign?pickedNextAvailable=false&availability=${r.availability}&prefix=${r.prefix
                        }&location=${r.location}&type=${r.type}&letters=${r.reservationType == Acma_amateurradioreservationtypes.SpecialEvent ? r.callsignName : r.letters
                        }`)
                      }
                    />
                  ) : (
                    <></>
                  ),
              },
            ]}
            noRowsMessage="No call signs matching selected filters."
          />
        </InfiniteScroll>
      </Spinner>
    </>
  );
};
